'use client';

import { useRef, type ReactNode } from 'react';
import Link from 'next/link';
import { ArrowLeft, Share2 } from 'lucide-react';
import { useLocalHref } from '../navigation/base-path';
import { cn } from '../utils/cn';

export type ProfileScreenTabValue =
  | 'publications'
  | 'history'
  | 'partnership';

const profileTabs: {
  value: ProfileScreenTabValue;
  label: string;
  activeColor: string;
}[] = [
  { value: 'publications', label: 'Публикации', activeColor: 'text-primary' },
  { value: 'history', label: 'История', activeColor: 'text-accent' },
  {
    value: 'partnership',
    label: 'Партнерство',
    activeColor: 'text-brand-orange',
  },
];

interface ProfileScreenProps {
  firstName?: string | null;
  username?: string | null;
  photoUrl?: string | null;
  balance?: string | null;
  totalGenerations: number;
  postsCount: number;
  totalLikes: number;
  activeTab: ProfileScreenTabValue;
  /**
   * When set, renders a back arrow to the left of "Мой профиль". Miniapp
   * leaves this undefined because Telegram renders a native BackButton,
   * but on the web we need an explicit affordance.
   */
  onBack?: () => void;
  onShareProfile: () => void;
  onTabChange: (tab: ProfileScreenTabValue) => void;
  publicationsContent: ReactNode;
  historyContent: ReactNode;
  partnershipContent: ReactNode;
}

export function ProfileScreen({
  firstName,
  username,
  photoUrl,
  balance,
  totalGenerations,
  postsCount,
  totalLikes,
  activeTab,
  onBack,
  onShareProfile,
  onTabChange,
  publicationsContent,
  historyContent,
  partnershipContent,
}: ProfileScreenProps) {
  const tabsRef = useRef<HTMLDivElement>(null);
  const creditsHref = useLocalHref('/credits');

  const handleTabChange = (tab: ProfileScreenTabValue, index: number) => {
    onTabChange(tab);

    const nav = tabsRef.current;
    const button = nav?.children[index] as HTMLElement | undefined;
    if (!nav || !button) return;

    const navRect = nav.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();

    if (buttonRect.right > navRect.right) {
      nav.scrollBy({
        left: buttonRect.right - navRect.right + 16,
        behavior: 'smooth',
      });
    }

    if (buttonRect.left < navRect.left) {
      nav.scrollBy({
        left: buttonRect.left - navRect.left - 16,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="flex flex-col">
      <div className="relative z-10 profile-header">
        <div className="flex h-[67px] items-center justify-between gap-3 px-5">
          <div className="flex items-center gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label="Назад"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#242424]/20"
              >
                <ArrowLeft className="h-5 w-5 text-white" />
              </button>
            )}
            <span className="font-benzin text-base font-semibold text-white">
              Мой Профиль
            </span>
          </div>
          <button
            onClick={onShareProfile}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-[#242424]/20"
          >
            <Share2 className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      <div className="relative flex flex-col items-center">
        <div className="relative z-[1] mt-4 h-[100px] w-[100px] overflow-hidden rounded-full border-[1.5px] border-accent bg-card">
          {photoUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={photoUrl}
              alt={firstName || ''}
              className="h-full w-full animate-in object-cover fade-in duration-300"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-[#4e4e50] text-3xl font-bold text-white">
              {firstName?.[0] || '?'}
            </div>
          )}
        </div>

        <h1 className="mt-3 font-benzin text-2xl font-semibold text-white">
          {firstName || ''}
        </h1>
        {username && (
          <p className="mt-1 text-xs text-white/40">@{username}</p>
        )}

        <div className="mt-4 flex h-[45px] items-center justify-center gap-2 rounded-[22px] border border-white/10 bg-[#202020]/60 px-4 shadow-[0_4px_4px_rgba(0,0,0,0.25)]">
          <Link href={creditsHref} className="flex items-center gap-[5px]">
            <span className="font-benzin text-base font-semibold leading-none text-white">
              Баланс
            </span>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/assets/gem-icon.svg" alt="" className="h-4 w-4" />
            <span className="font-benzin text-base font-semibold leading-none text-primary">
              {balance || '0'}
            </span>
          </Link>
          <Link href={creditsHref}>
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
              <defs>
                <linearGradient id="addBtnGrad" x1="8.5" y1="0" x2="8.5" y2="17" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0.12" />
                  <stop offset="100%" stopColor="#ffffff" stopOpacity="0.04" />
                </linearGradient>
              </defs>
              <circle cx="8.5" cy="8.5" r="8.35" fill="url(#addBtnGrad)" stroke="rgba(255,255,255,0.12)" strokeWidth="0.3" />
              <rect x="4.5" y="7.25" width="8" height="2.5" fill="white" />
              <rect x="7.25" y="4.5" width="2.5" height="8" fill="white" />
            </svg>
          </Link>
        </div>
      </div>

      <div className="mx-[23px] mt-5 flex items-center border-t border-white/[0.08] pt-3">
        <div className="flex flex-1 flex-col items-center">
          <span className="text-lg tracking-tight text-white">
            {totalGenerations}
          </span>
          <span className="text-[10px] text-white/30">Генерации</span>
        </div>
        <div className="h-[38px] w-px bg-white/[0.08]" />
        <div className="flex flex-1 flex-col items-center">
          <span className="text-lg tracking-tight text-white">
            {postsCount}
          </span>
          <span className="text-[10px] text-white/30">Публикации</span>
        </div>
        <div className="h-[38px] w-px bg-white/[0.08]" />
        <div className="flex flex-1 flex-col items-center">
          <span className="text-lg tracking-tight text-white">
            {formatCount(totalLikes)}
          </span>
          <span className="text-[10px] text-white/30">Лайков</span>
        </div>
      </div>

      <div
        ref={tabsRef}
        className="flex gap-4 overflow-x-auto px-4 pb-4 pt-4 scrollbar-hide"
        style={{ scrollPaddingInline: '16px' }}
      >
        {profileTabs.map((tab, index) => (
          <button
            key={tab.value}
            onClick={() => handleTabChange(tab.value, index)}
            className={cn(
              'flex-shrink-0 whitespace-nowrap font-benzin text-xl font-semibold transition-colors duration-200',
              activeTab === tab.value ? tab.activeColor : 'text-[#8e8e93]',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div>
        {activeTab === 'publications' && publicationsContent}
        {activeTab === 'history' && historyContent}
        {activeTab === 'partnership' && partnershipContent}
      </div>
    </div>
  );
}

function formatCount(value: number) {
  return value >= 1000 ? `${(value / 1000).toFixed(1)}K` : String(value);
}
